/**
 * WhatsApp text formatting → safe Preact nodes.
 *
 * Render messages with the same minimal markdown WhatsApp supports:
 *   *bold*           → <strong>
 *   _italic_         → <em>
 *   ~strike~         → <s>
 *   `code`           → <code>
 *   ```block```      → <code> (multi-line block)
 *   > quote          → <blockquote>
 *   URLs / phone     → <a>
 *
 * Built without dangerouslySetInnerHTML so untrusted text never reaches the
 * DOM as markup. We tokenize, then return Preact nodes; the caller drops the
 * array into a span/div and lets Preact escape values.
 *
 * Used by text bubbles, media captions, template bodies, and interactive
 * body text. Keeps a single source of truth so chatbot replies, agent
 * messages, and template variables all look identical.
 */

import { html } from 'htm/preact';

// Per WhatsApp's official formatting rules, the delimiter must touch a
// non-space character on both sides (so a literal `a * b * c` stays plain).
// The capturing groups isolate the inner text; the lookarounds (or their
// non-supporting fallbacks) anchor the boundary.
const TOKEN_RE = new RegExp(
  [
    '```([\\s\\S]+?)```',                              // 1: code block
    '`([^`\\n]+)`',                                    // 2: inline code
    '\\*([^*\\n][^*\\n]*?)\\*',                        // 3: bold
    '_([^_\\n][^_\\n]*?)_',                            // 4: italic
    '~([^~\\n][^~\\n]*?)~',                            // 5: strike
    '(https?:\\/\\/[^\\s<>"\']+)',                     // 6: url
    '(www\\.[^\\s<>"\']+)',                            // 7: bare www
  ].join('|'),
  'g'
);

const STYLE_CODE_INLINE = 'background:rgba(255,255,255,0.08);padding:0 4px;border-radius:3px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.88em';
const STYLE_CODE_BLOCK  = 'display:block;background:rgba(0,0,0,0.25);padding:6px 8px;border-radius:6px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.85em;white-space:pre-wrap;margin:2px 0;overflow-x:auto';
const STYLE_LINK        = 'color:inherit;text-decoration:underline;text-underline-offset:2px';

function _ensureScheme(url) {
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

/**
 * Format one chunk of WA-style markdown to Preact nodes. Returns an array
 * of strings + html`` fragments. Safe to drop straight into JSX.
 *
 * Strategy: regex-tokenize once. Anything between matches stays as plain
 * text (Preact will escape it). Anything inside a match becomes a styled
 * node — and we DON'T recurse for now: WhatsApp itself doesn't render
 * nested formatting (`*_both_*` stays literal-ish), so this matches the
 * platform behavior and keeps the helper trivial.
 */
export function formatWaText(text) {
  if (!text) return [];
  const out  = [];
  const src  = String(text);
  let lastIx = 0;
  let m;
  TOKEN_RE.lastIndex = 0;

  while ((m = TOKEN_RE.exec(src)) !== null) {
    // Push the literal text BEFORE this match
    if (m.index > lastIx) out.push(src.slice(lastIx, m.index));

    const [, codeBlock, codeInline, bold, italic, strike, url, www] = m;
    if      (codeBlock != null)  out.push(html`<code style=${STYLE_CODE_BLOCK}>${codeBlock}</code>`);
    else if (codeInline != null) out.push(html`<code style=${STYLE_CODE_INLINE}>${codeInline}</code>`);
    else if (bold != null)       out.push(html`<strong>${bold}</strong>`);
    else if (italic != null)     out.push(html`<em>${italic}</em>`);
    else if (strike != null)     out.push(html`<s>${strike}</s>`);
    else if (url != null)        out.push(html`<a href=${url} target="_blank" rel="noopener noreferrer" style=${STYLE_LINK} onClick=${(e) => e.stopPropagation()}>${url}</a>`);
    else if (www != null)        out.push(html`<a href=${_ensureScheme(www)} target="_blank" rel="noopener noreferrer" style=${STYLE_LINK} onClick=${(e) => e.stopPropagation()}>${www}</a>`);

    lastIx = m.index + m[0].length;
  }

  // Tail (any plain text after the last match)
  if (lastIx < src.length) out.push(src.slice(lastIx));

  return out;
}

/**
 * Lightweight helper for callers that already render text in a `<span>`
 * with `white-space:pre-wrap`. Wraps the formatted nodes so the bubble
 * preserves newlines while honoring the inline formatting.
 */
export function FormattedText({ text, style = 'white-space:pre-wrap' }) {
  return html`<span style=${style}>${formatWaText(text)}</span>`;
}
